import { useRoute } from '#imports'
import { computed } from 'vue'

import type { ChatRequestPageContext } from '~/api/shopping-assistant/types'

import { ENTRY_POINTS } from '../constants'
import { sanitizeSeededMessage } from '../utils/sanitizeSeededMessage'

export type EntryPoint = (typeof ENTRY_POINTS)[number]

function isValidEntryPoint(value: string): value is EntryPoint {
  return ENTRY_POINTS.includes(value as EntryPoint)
}

/**
 * Manages the context a chat is started with.
 */
export function useShoppingAssistantContext() {
  const route = useRoute()

  const pageContext = computed<ChatRequestPageContext | null>(() => {
    const productId = route.query.product_id

    if (typeof productId === 'string') {
      return {
        type: 'product_page',
        product_id: productId,
      }
    }

    return null
  })

  const entryPoint = computed<EntryPoint>(() =>
    typeof route.query.entry_point === 'string' &&
    isValidEntryPoint(route.query.entry_point)
      ? route.query.entry_point
      : 'seeded_link',
  )

  // Initial message to auto-send on a seeded launch, trimmed and capped.
  // Empty string when absent (or whitespace-only) — no message is sent.
  const seededMessage = computed<string>(() =>
    sanitizeSeededMessage(route.query.message),
  )

  // Attribution source for a seeded link. Empty string when absent.
  const entrySource = computed<string>(() => {
    const value = route.query.message_source

    return typeof value === 'string' ? value.trim() : ''
  })

  // A seeded launch opens a brand-new chat pre-supplied with page context
  // and/or an initial message (push notification, email, in-page CTA). It never
  // targets an existing chat: when the route carries a chatId, that wins and the
  // seeded query params are ignored.
  const isSeededLaunch = computed<boolean>(() => {
    if (typeof route.params.chatId === 'string') {
      return false
    }

    return pageContext.value !== null || seededMessage.value !== ''
  })

  return {
    pageContext,
    entryPoint,
    seededMessage,
    entrySource,
    isSeededLaunch,
  }
}
